import React, { useRef } from "react";

/**
 * One exhibit. Video tiles show the clip itself and play on hover. A work
 * can optionally set media.poster for a custom thumbnail image; without one,
 * the video's own first frame is the thumbnail. Clicking opens the work
 * full-size in the centered lightbox (see OurWorks.jsx).
 */
const MediaCard = ({ work, index, col, rowStart, rowSpan, onExpand }) => {
  const frameRef = useRef(null);
  const raf = useRef(0);

  const { media } = work;

  // Magnetic drift: the media leans a few pixels toward the pointer.
  const handleMove = (e) => {
    const frame = frameRef.current;
    if (!frame || raf.current) return;

    raf.current = requestAnimationFrame(() => {
      raf.current = 0;
      const rect = frame.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      frame.style.setProperty("--mx", `${(x * 14).toFixed(2)}px`);
      frame.style.setProperty("--my", `${(y * 14).toFixed(2)}px`);
    });
  };

  const handleLeave = () => {
    const frame = frameRef.current;
    if (!frame) return;
    frame.style.setProperty("--mx", "0px");
    frame.style.setProperty("--my", "0px");
    frame.querySelector("video")?.pause();
  };

  // Hover-play is driven by the frame, not the <video> itself — the caption
  // overlay sits on top of the video on hover, and a handler on the video
  // alone would see that as a mouse-leave and pause mid-hover.
  const handleEnter = () => {
    frameRef.current?.querySelector("video")?.play().catch(() => {});
  };

  return (
    <article
      className="ow-tile"
      style={{ "--col": col, "--row": `${rowStart} / span ${rowSpan}`, "--i": index }}
      data-tile
    >
      <div
        className="ow-frame"
        ref={frameRef}
        onMouseEnter={handleEnter}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        onClick={() => onExpand?.(work)}
      >
        <div className="ow-media" data-parallax>
          {media.type === "video" ? (
            <video
              src={media.src}
              poster={media.poster || undefined}
              muted
              loop
              playsInline
              preload={media.poster ? "metadata" : "auto"}
              style={{ objectPosition: media.focal || "center" }}
            />
          ) : (
            <img
              src={media.src}
              alt={work.title}
              loading="lazy"
              decoding="async"
              style={{ objectPosition: media.focal || "center" }}
            />
          )}
        </div>

        <span className="ow-veil" aria-hidden="true" />

        <div className="ow-caption">
          <h2 className="ow-title">{work.title}</h2>
          <p className="ow-meta">
            <span>{work.category}</span>
            <i aria-hidden="true" />
            <span>{work.year}</span>
          </p>
        </div>
      </div>
    </article>
  );
};

export default MediaCard;
